import React, { useState } from 'react';
import { View, TouchableOpacity } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';

import { Text } from '@/components/ui/text';
import { WebAppShell } from '@/design-system/components';
import { CreditPurchaseModal } from '@/components/credits';
import { useAuth } from '../../context/AuthContext';
import { useCredits } from '../../context/CreditContext';
import { useToast } from '../../context/ToastContext';
import { useTrialContext } from '../../context/TrialContext';
import { useResponsive } from '../../hooks/useResponsive';
import { useTranslation } from '../../hooks/useTranslation';

import { useAccountForms } from './hooks/useAccountForms';
import { useCancelSubscription } from './hooks/useCancelSubscription';
import { useChangePlan } from './hooks/useChangePlan';
import { useContractWithdrawal } from './hooks/useContractWithdrawal';
import { useLanguagePreference } from './hooks/useLanguagePreference';
import { useLogoutConfirm } from './hooks/useLogoutConfirm';
import { useProfileData } from './hooks/useProfileData';
import { useSubscriptionActions } from './hooks/useSubscriptionActions';
import { CancelSubscriptionModal } from './modals/CancelSubscriptionModal';
import { ChangePlanModal } from './modals/ChangePlanModal';
import { WithdrawContractModal } from './modals/WithdrawContractModal';
import { DeleteAccountModal } from './modals/DeleteAccountModal';
import { EditProfileModal } from './modals/EditProfileModal';
import { LanguageSelectModal } from './modals/LanguageSelectModal';
import { LogoutConfirmModal } from './modals/LogoutConfirmModal';
import { deriveSubscriptionFlags } from './planBilling';
import { COLORS, profileWebStyles } from './profileStyles';
import { AccountDetailsCard } from './sections/AccountDetailsCard';
import { AccountSecurityPanel } from './sections/AccountSecurityPanel';
import { CoachingProfilePanel } from './sections/CoachingProfilePanel';
import { ContractWithdrawalCard } from './sections/ContractWithdrawalCard';
import { LanguagePreferencesPanel } from './sections/LanguagePreferencesPanel';
import { PrivacyDataPanel } from './sections/PrivacyDataPanel';
import { ProfileShellState } from './sections/ProfileShellState';
import { SubscriptionCard } from './sections/SubscriptionCard';
import { SubscriptionNoticeBanners } from './sections/SubscriptionNoticeBanners';

/**
 * Profile & subscription screen. Composes the section components under
 * `sections/` and the modals under `modals/`; state and handlers live in the
 * hooks under `hooks/`, pure logic in `planBilling.ts` / `profileFormat.ts`.
 */
export default function UserProfileScreenEnhanced() {
  const { logout, refreshAuth, billingStatus } = useAuth();
  const router = useRouter();
  const { t, language, changeLanguage, availableLanguages } = useTranslation();
  const { showToast } = useToast();
  const { width: windowWidth, isPhone, isCompact } = useResponsive();
  const { creditStatus, refreshStatus: refreshCredits } = useCredits();
  const { trialStatus } = useTrialContext();

  const [showCreditPurchase, setShowCreditPurchase] = useState(false);

  const {
    showLogoutConfirm,
    logoutLoading,
    handleLogout,
    handleConfirmLogout,
    closeLogoutConfirm,
  } = useLogoutConfirm(logout);

  const { profile, loading, loadProfile } = useProfileData({
    t,
    // Re-seed the section forms from every fresh load. Runs after this render
    // has finished, so `account` and `plan` below are initialized.
    onLoaded: (data) => {
      account.syncEditForm(data);
      plan.syncPlanForm(data);
    },
  });
  const account = useAccountForms({ profile, loadProfile, logout, showToast, t });
  const plan = useChangePlan({ profile, loadProfile, refreshAuth, router, language, t });
  const flags = deriveSubscriptionFlags(profile, trialStatus);
  const { isCardBackedTrial, shouldResubscribe } = flags;
  const languagePreference = useLanguagePreference({ changeLanguage, showToast, t });
  const withdrawal = useContractWithdrawal({ billingStatus, refreshAuth, loadProfile, showToast, t });
  const cancelFlow = useCancelSubscription({
    profile,
    trialStatus,
    isCardBackedTrial,
    loadProfile,
    t,
  });
  const {
    handleManageSubscription,
    handleChoosePlan,
    handleResumeSubscription,
    resumeLoading,
  } = useSubscriptionActions({
    profile,
    isCardBackedTrial,
    loadProfile,
    redirectDeadSubscriptionToCheckout: plan.redirectDeadSubscriptionToCheckout,
    router,
    t,
  });

  if (loading) {
    return <ProfileShellState kind="loading" onRetry={loadProfile} t={t} />;
  }

  if (!profile) {
    return <ProfileShellState kind="error" onRetry={loadProfile} t={t} />;
  }

  return (
    <>
      <WebAppShell
        active="profile"
        contentMaxWidth="100%"
        topRight={
          <TouchableOpacity
            testID="profile_logout_button"
            style={profileWebStyles.logoutButton}
            onPress={handleLogout}
            accessibilityRole="button"
          >
            <MaterialIcons name="logout" size={18} color={COLORS.error} />
            <Text style={profileWebStyles.logoutText}>{t('profile.webLogout')}</Text>
          </TouchableOpacity>
        }
      >
        <View style={profileWebStyles.pageHeader}>
          <View>
            <Text style={profileWebStyles.pageTitle}>{t('profile.webTitle')}</Text>
            <Text style={profileWebStyles.pageSubtitle}>
              {t('profile.webSubtitle')}
            </Text>
          </View>
        </View>

        <AccountDetailsCard
          profile={profile}
          isTiny={isPhone}
          onEditProfile={account.openEditProfile}
          t={t}
        />

        <View style={[profileWebStyles.twoColumnGrid, isCompact && profileWebStyles.twoColumnGridCompact]}>
          <LanguagePreferencesPanel
            language={language}
            availableLanguages={availableLanguages}
            onOpenLanguageSelect={languagePreference.openLanguageSelect}
            isCompact={isCompact}
            t={t}
          />

          <AccountSecurityPanel
            flags={flags}
            isCompact={isCompact}
            onManageSubscription={handleManageSubscription}
            onOpenCancelSubscription={cancelFlow.handleOpenCancelSubscription}
            onChoosePlan={handleChoosePlan}
            t={t}
          />
        </View>

        <SubscriptionCard
          profile={profile}
          flags={flags}
          trialStatus={trialStatus}
          isCompact={isCompact}
          isPhone={isPhone}
          formatCurrency={plan.formatCurrency}
          purchasedCredits={creditStatus?.purchased_credits ?? 0}
          resumeLoading={resumeLoading}
          onOpenChangePlan={plan.handleOpenChangePlan}
          onManageSubscription={handleManageSubscription}
          onChoosePlan={handleChoosePlan}
          onResumeSubscription={handleResumeSubscription}
          onOpenCancelSubscription={cancelFlow.handleOpenCancelSubscription}
          onBuyCredits={() => setShowCreditPurchase(true)}
          t={t}
        />

        <ContractWithdrawalCard withdrawal={withdrawal} t={t} />

        <View style={[profileWebStyles.twoColumnGrid, isCompact && profileWebStyles.twoColumnGridCompact]}>
          <PrivacyDataPanel
            onDeleteAccount={account.openDeleteAccount}
            showToast={showToast}
            isCompact={isCompact}
            t={t}
          />

          <CoachingProfilePanel language={language} isCompact={isCompact} t={t} />
        </View>

        <SubscriptionNoticeBanners
          profile={profile}
          shouldResubscribe={shouldResubscribe}
          isCardBackedTrial={isCardBackedTrial}
          isPhone={isPhone}
          resumeLoading={resumeLoading}
          onResumeSubscription={handleResumeSubscription}
          t={t}
        />
      </WebAppShell>

      <EditProfileModal
        visible={account.showEditProfile}
        form={account.editForm}
        onChangeForm={account.setEditForm}
        loading={account.formLoading}
        onCancel={account.closeEditProfile}
        onSave={account.handleEditProfile}
        t={t}
      />
      <DeleteAccountModal
        visible={account.showDeleteAccount}
        form={account.deleteForm}
        onChangeForm={account.setDeleteForm}
        loading={account.formLoading}
        onCancel={account.closeDeleteAccount}
        onDelete={account.handleDeleteAccount}
        t={t}
      />
      <CancelSubscriptionModal
        cancelFlow={cancelFlow}
        isCardBackedTrial={isCardBackedTrial}
        t={t}
      />
      <WithdrawContractModal withdrawal={withdrawal} t={t} />
      <ChangePlanModal
        plan={plan}
        currentPlanTier={profile.subscription?.plan_tier}
        windowWidth={windowWidth}
        t={t}
      />
      <LanguageSelectModal
        visible={languagePreference.showLanguageSelect}
        language={language}
        availableLanguages={availableLanguages}
        onSelect={languagePreference.handleSelectLanguage}
        onClose={languagePreference.closeLanguageSelect}
        t={t}
      />
      <LogoutConfirmModal
        visible={showLogoutConfirm}
        loading={logoutLoading}
        onCancel={closeLogoutConfirm}
        onConfirm={handleConfirmLogout}
        t={t}
      />
      <CreditPurchaseModal
        visible={showCreditPurchase}
        onClose={() => setShowCreditPurchase(false)}
        onPurchaseComplete={() => {
          setShowCreditPurchase(false);
          refreshCredits();
        }}
      />
    </>
  );
}
